/**
 * 气泡文本的富文本切分（21l）：把纯文本切成「普通文本 / 可点链接 / @提及」三种段。
 * 纯函数，渲染在 `MessageList`。
 *
 * 口径逐条照旧版 `widgets/message_bubble.dart:2077~2175`：
 * - **先切链接、再在非链接段里切提及**（顺序不能反，否则 `http://a.com/@b` 里的
 *   `@b` 会被当提及切掉）；
 * - 链接正则 `(https?://[^\s]+|www\.[^\s]+)`，大小写不敏感；
 * - **结尾标点吐回正文**：`去 abc.com。` 的句号不属于链接（旧版 `_trailingPunct`，
 *   中英文标点与各种引号括号都在内）；吐回的那段仍要再过一次提及切分；
 * - 提及正则 `@[^\s@]+`，**按文档顺序消费 `mentionTargets`**——服务端只给了一串
 *   目标 uid、不带位置，靠顺序对齐（旧版 `_mentionTargetCursor` 同法）。
 *   取不到或 ≤0 的目标仍然高亮但不可点（旧版 recognizer 为 null 同义）。
 *
 * ⚠️ **安全：只放行 http/https**。把文本变成 `<a href>` 是经典 XSS 入口——
 * `javascript:alert(1)` 这类串一旦被当链接渲染就可执行。当前正则本身已经限死了
 * 协议，这里再用 URL 解析显式复核一次（纵深防御 + 防后人改正则时踩雷）。
 * 渲染侧还要配 `rel="noopener noreferrer"`，见 MessageList。
 *
 * 性能：`buildTimelineItems` 每批重建所有条目（20e 轮实测过这条热路径），
 * 所以先用 `indexOf` 做**零成本粗筛**——绝大多数消息既没有 `@` 也没有链接，
 * 直接返回单段，不进正则。这是 20e「先看首字符再 parse」那条口径的同款做法。
 */

export type RichSegment =
  | { readonly kind: 'text'; readonly text: string }
  | { readonly kind: 'link'; readonly text: string; readonly href: string }
  /** userId=0 表示对不上目标：高亮但不可点（旧版同口径） */
  | { readonly kind: 'mention'; readonly text: string; readonly userId: number }
  /** 长数字串（邀请码/群号/手机号）：只在公告里切，点一下复制。见 parseNoticeText */
  | { readonly kind: 'number'; readonly text: string };

const LINK_RE = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
const MENTION_RE = /@[^\s@]+/g;
/** 结尾标点（旧版 _trailingPunct 逐字） */
const TRAILING_PUNCT_RE = /[),.;!?，。！？、）】"'”’]+$/;

/** 只放行 http/https；返回 null = 不当链接渲染 */
function safeHref(raw: string): string | null {
  const candidate = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  try {
    const url = new URL(candidate);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : null;
  } catch {
    return null;
  }
}

/** 对一段**不含链接**的文本切提及；cursor 以对象传递，跨段连续消费 */
function pushMentions(
  out: RichSegment[],
  segment: string,
  targets: readonly number[],
  cursor: { i: number },
): void {
  if (segment === '') return;
  if (!segment.includes('@')) {
    out.push({ kind: 'text', text: segment });
    return;
  }
  MENTION_RE.lastIndex = 0;
  let at = 0;
  for (let m = MENTION_RE.exec(segment); m !== null; m = MENTION_RE.exec(segment)) {
    if (m.index > at) out.push({ kind: 'text', text: segment.slice(at, m.index) });
    // 目标按顺序消费：没有更多目标时记 0（高亮不可点）
    const userId = cursor.i < targets.length ? (targets[cursor.i] ?? 0) : 0;
    if (cursor.i < targets.length) cursor.i += 1;
    out.push({ kind: 'mention', text: m[0], userId: userId > 0 ? userId : 0 });
    at = m.index + m[0].length;
  }
  if (at < segment.length) out.push({ kind: 'text', text: segment.slice(at) });
}

export function parseRichText(text: string, mentionTargets: readonly number[]): RichSegment[] {
  // 零成本粗筛（文件头「性能」）：绝大多数消息直接走这条
  if (!text.includes('@') && !text.includes('://') && !text.includes('www.')) {
    return [{ kind: 'text', text }];
  }
  const out: RichSegment[] = [];
  const cursor = { i: 0 };
  LINK_RE.lastIndex = 0;
  let at = 0;
  for (let m = LINK_RE.exec(text); m !== null; m = LINK_RE.exec(text)) {
    if (m.index > at) pushMentions(out, text.slice(at, m.index), mentionTargets, cursor);
    let raw = m[0];
    // 结尾标点吐回正文（旧版同口径），吐回的部分仍要过提及切分
    let trailing = '';
    const t = TRAILING_PUNCT_RE.exec(raw);
    if (t !== null) {
      trailing = raw.slice(t.index);
      raw = raw.slice(0, t.index);
    }
    const href = raw === '' ? null : safeHref(raw);
    if (href === null) {
      // 协议不过白名单（或裁完空了）：当普通文本，绝不渲染成链接
      pushMentions(out, raw, mentionTargets, cursor);
    } else {
      out.push({ kind: 'link', text: raw, href });
    }
    if (trailing !== '') pushMentions(out, trailing, mentionTargets, cursor);
    at = m.index + m[0].length;
  }
  if (at < text.length) pushMentions(out, text.slice(at), mentionTargets, cursor);
  return out;
}

/**
 * 群公告文本的切分（21al；人工指图「链接也要解析、数字最好加亮一下」）。
 *
 * 复用 `parseRichText` 的全部口径（链接正则/结尾标点吐回/协议白名单），再把
 * **非链接**文本段里的长数字串切成可点的 `number` 段：
 * - 公告没有 `mentionTargets`（服务端只存一段纯文本，不带 @ 目标表），传空数组
 *   → `@xxx` 仍然高亮但不可点，与「对不上目标」同口径；
 * - **≥6 位连续数字**才算——公告里真正要人抄走的是邀请码/群号/手机号这类，
 *   「5元」「12年」「48.8倍」全标上只会糊成一片，反倒找不到那串码；
 * - 链接段不再二次切分，否则 `https://168wl.app/123456` 的尾号会被从链接里
 *   抠出来，点了既不跳也复制错。
 */
const NOTICE_NUMBER_RE = /\d{6,}/g;

function pushNumbers(out: RichSegment[], text: string): void {
  NOTICE_NUMBER_RE.lastIndex = 0;
  let at = 0;
  for (let m = NOTICE_NUMBER_RE.exec(text); m !== null; m = NOTICE_NUMBER_RE.exec(text)) {
    if (m.index > at) out.push({ kind: 'text', text: text.slice(at, m.index) });
    out.push({ kind: 'number', text: m[0] });
    at = m.index + m[0].length;
  }
  if (at < text.length) out.push({ kind: 'text', text: text.slice(at) });
}

export function parseNoticeText(text: string): RichSegment[] {
  const out: RichSegment[] = [];
  for (const seg of parseRichText(text, [])) {
    // 零成本粗筛：没有数字的段（公告里绝大多数行）原样过
    if (seg.kind !== 'text' || !/\d/.test(seg.text)) {
      out.push(seg);
      continue;
    }
    pushNumbers(out, seg.text);
  }
  return out;
}
